import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell,
  TableBody, TablePagination, CircularProgress, Alert, Chip,
  TextField, InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { API_BASE_URL } from "../../config";

const OP_COLORS = {
  CREATE: { bg: "#e8f5e9", color: "#2e7d32" },
  UPDATE: { bg: "#e3f2fd", color: "#1565c0" },
  DELETE: { bg: "#fdecea", color: "#c62828" },
  ACTION: { bg: "#fff3e0", color: "#e65100" },
};

function opStyle(op) {
  return OP_COLORS[op?.toUpperCase()] || { bg: "#f5f5f5", color: "#616161" };
}

export default function ClientActivityPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/client/activity?limit=200`);
      setEvents(res.data.events || []);
    } catch {
      setError("Failed to load activity log. Keycloak event logging may not be enabled.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = events.filter((e) => {
    const q = search.toLowerCase();
    return !q ||
      e.resourcePath?.toLowerCase().includes(q) ||
      e.operationType?.toLowerCase().includes(q) ||
      e.authDetails?.realmId?.toLowerCase().includes(q);
  });

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Activity Log</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Recent admin events in your organisation realm
      </Typography>

      {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search resource, operation…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 300, bgcolor: "#fff" }}
        />
      </Box>

      <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              {["Time", "Operation", "Resource Type", "Resource Path", "Client"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 600, fontSize: "12px", color: "#667085" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {events.length === 0
                    ? "No activity events. Enable admin events in Keycloak realm settings."
                    : "No matching events."}
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((e, idx) => {
                const style = opStyle(e.operationType);
                return (
                  <TableRow key={idx} hover>
                    <TableCell sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>
                      {e.time ? new Date(e.time).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={e.operationType || "—"}
                        size="small"
                        sx={{ bgcolor: style.bg, color: style.color, fontWeight: 600, fontSize: "11px" }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px" }}>{e.resourceType || "—"}</TableCell>
                    <TableCell sx={{ fontSize: "12px", fontFamily: "monospace", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {e.resourcePath || "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: "12px" }}>{e.authDetails?.clientId || "—"}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>
    </Box>
  );
}
